/**
 * Discreet "you don't have a button yet" banner for the try-before-buy
 * path. Mounted in AppShell, shown whenever the current account has
 * zero claimed devices. Opens the shared AddDeviceDialog, which branches
 * between pairing a button you already have and ordering one — so the
 * banner no longer assumes ordering. Hidden once a device is claimed.
 *
 * Intentionally subtle — orange/amber tint, single line, no big CTA.
 * The dashboard is still useful without a device, so the banner
 * shouldn't dominate.
 */

import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useDevices } from '@/hooks/useDevices';
import { AddDeviceDialog } from '@/components/AddDeviceDialog';

interface Props {
  aid: string | null;
}

export function NoDeviceBanner({ aid }: Props) {
  const { devices, loading } = useDevices(aid);
  const location = useLocation();
  const [addOpen, setAddOpen] = useState(false);
  // SetupDevice navigates here with `justClaimed` right after a successful
  // claim. The device mirror exists, but the devices onSnapshot may first
  // return an empty *cached* result before the server snapshot arrives —
  // without this guard the banner flashes "no button" on the landing page
  // and only clears once you navigate (which re-reads a now-warm cache).
  // A just-claimed device means we definitely have one, so suppress it.
  const justClaimed =
    (location.state as { justClaimed?: boolean } | null)?.justClaimed === true;
  if (!aid || loading || justClaimed || devices.length > 0) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <span>You don't have a button linked yet.</span>
      <button
        type="button"
        onClick={() => setAddOpen(true)}
        className="font-medium text-amber-900 underline hover:no-underline"
      >
        Add a button →
      </button>
      <AddDeviceDialog open={addOpen} onOpenChange={setAddOpen} aid={aid} />
    </div>
  );
}
